import type {
  BackendApiEndpoint,
  BackendApiTestCase,
} from "./backend-api-coverage-types";

export interface TestFile {
  linkedEndpointIds: string[];
  path: string;
  tests: BackendApiTestCase[];
  type: TestType;
}

export type TestType =
  | "api_unit"
  | "unit"
  | "integration"
  | "security"
  | "critical_path"
  | "performance"
  | "desktop_e2e"
  | "maestro"
  | "other";

export function buildFiles(tests: BackendApiTestCase[]): TestFile[] {
  const byPath = new Map<string, BackendApiTestCase[]>();
  for (const testCase of tests) {
    const items = byPath.get(testCase.source_path) ?? [];
    items.push(testCase);
    byPath.set(testCase.source_path, items);
  }
  return [...byPath.entries()]
    .map(([path, fileTests]) => ({
      path,
      tests: fileTests,
      type: testTypeFromPath(path),
      linkedEndpointIds: unique(
        fileTests.flatMap((test) => test.linked_endpoint_ids),
      ),
    }))
    .sort(
      (a, b) => a.type.localeCompare(b.type) || a.path.localeCompare(b.path),
    );
}

export function filesByTestType(files: TestFile[]): Map<TestType, TestFile[]> {
  const out = new Map<TestType, TestFile[]>();
  for (const file of files) {
    const items = out.get(file.type) ?? [];
    items.push(file);
    out.set(file.type, items);
  }
  return out;
}

export function filterFiles(files: TestFile[], query: string): TestFile[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return files;
  return files.filter((file) =>
    `${file.path}\n${file.tests
      .map((testCase) => `${testCase.title}\n${testCase.logic}`)
      .join("\n")}`
      .toLowerCase()
      .includes(normalized),
  );
}

export function typeStats(files: TestFile[]): { files: number; tests: number } {
  return {
    files: files.length,
    tests: files.reduce((sum, file) => sum + file.tests.length, 0),
  };
}

export function endpointMap(
  endpoints: BackendApiEndpoint[],
): Map<string, BackendApiEndpoint> {
  return new Map(endpoints.map((endpoint) => [endpoint.id, endpoint]));
}

export function linkedEndpoints(
  ids: string[],
  endpointsById: Map<string, BackendApiEndpoint>,
): BackendApiEndpoint[] {
  return ids
    .map((id) => endpointsById.get(id))
    .filter(
      (endpoint): endpoint is BackendApiEndpoint => endpoint !== undefined,
    );
}

export function testTypeFromPath(path: string): TestType {
  if (path.startsWith("tests/unit/api/")) return "api_unit";
  if (path.startsWith("tests/unit/")) return "unit";
  if (path.startsWith("tests/integration/")) return "integration";
  if (path.startsWith("tests/security/")) return "security";
  if (path.startsWith("tests/critical-path/")) return "critical_path";
  if (path.startsWith("tests/performance/") || path.startsWith("tests/perf/")) {
    return "performance";
  }
  if (path.startsWith("tests/e2e-desktop/")) return "desktop_e2e";
  if (path.startsWith(".maestro/flows/")) return "maestro";
  return "other";
}

export const testTypes: TestType[] = [
  "api_unit",
  "unit",
  "integration",
  "security",
  "critical_path",
  "performance",
  "desktop_e2e",
  "maestro",
  "other",
];

function unique(values: string[]): string[] {
  return [...new Set(values)];
}
